import React from "react"
import { Form, Select, Space, FormInstance } from "antd"
import { faFileCsv, faGear } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import FormatPanelHeader from "./FormatPanelHeader.component"
import { EXPORT_VARIABLE_TYPES, CSVFormValues, CSV_CAT_OPTION } from "types/engineExport.types"
import { InputValues } from "features/QueryConsole/api/useExportDataModal"
import {
  ExportScript,
  ExportScriptOption,
  toScriptOption,
  scriptNameFromOption,
} from "features/QueryConsole/api/useExportScripts"

interface CsvExportPanelProps {
  inputValues: InputValues
  onInputChange: <K extends keyof InputValues>(key: K, value: InputValues[K]) => void
  onExport: () => Promise<void>
  isExporting: boolean
  form: FormInstance<CSVFormValues>
  onOpenExportMap: (format: "csv") => void
  /** The unit's export scripts, listed as extra categorical formats (client formats) */
  exportScripts?: ExportScript[]
}

const CsvExportPanel: React.FC<CsvExportPanelProps> = ({
  inputValues,
  onInputChange,
  onExport,
  isExporting,
  form,
  onOpenExportMap,
  exportScripts = [],
}) => {
  // A client format runs on the export with the map, so it is exported from Configure Map
  const isClientFormat = scriptNameFromOption(inputValues.csvCatOption) !== null

  const handleCatOptionChange = (value: CSV_CAT_OPTION | ExportScriptOption) => {
    onInputChange("csvCatOption", value)
    // Client formats read the question labels from the map, which a Numbered export replaces
    if (scriptNameFromOption(value) !== null) {
      onInputChange("csvVariableType", EXPORT_VARIABLE_TYPES.LABELS)
      form.setFieldsValue({ variableType: EXPORT_VARIABLE_TYPES.LABELS })
    }
  }

  return (
    <div className="flex flex-col">
      <FormatPanelHeader
        icon={faFileCsv}
        title="Export as CSV"
        tagLabel="Universal"
        tagColor="orange"
        description="Comma-separated values. Widely compatible with spreadsheet software, databases, and data tools."
      />
      <Form layout="vertical" form={form}>
        <Form.Item
          label="Select Variable Type"
          name="variableType"
          rules={[{ required: true, message: "Please select a Variable Type" }]}
          extra={isClientFormat ? "Client formats are always Labelled." : undefined}
        >
          <Select
            disabled={isClientFormat}
            value={inputValues.csvVariableType || undefined}
            onChange={(value: EXPORT_VARIABLE_TYPES) =>
              onInputChange("csvVariableType", value)
            }
            options={[
              { value: EXPORT_VARIABLE_TYPES.NUMBERING, label: "Numbered" },
              { value: EXPORT_VARIABLE_TYPES.LABELS, label: "Labelled" },
            ]}
            placeholder="Select Variable Type"
          />
        </Form.Item>
        <Form.Item label="Categorical Export Format">
          <Select
            value={inputValues.csvCatOption}
            onChange={handleCatOptionChange}
            options={[
              { value: CSV_CAT_OPTION.NORMAL, label: "Normal (category codes, requires map file)" },
              { value: CSV_CAT_OPTION.EXPAND, label: "Expanded (boolean columns per category)" },
              { value: CSV_CAT_OPTION.SECOND_ROW, label: "Second Row (category definitions in row 2)" },
              { value: CSV_CAT_OPTION.LABELS_IN_DATA, label: "Labels in Data (human-readable labels)" },
              ...exportScripts.map((script) => ({
                value: toScriptOption(script.name),
                label: `${script.label} (client format)`,
              })),
            ]}
          />
        </Form.Item>
      </Form>
      <div className="border-t border-gray-100 pt-4 mt-1">
        <Space wrap>
          <DataQButton
            type="primary"
            onClick={onExport}
            isLoading={isExporting}
            isDisabled={isClientFormat}
            aria-label="Export data as CSV file"
          >
            Export CSV
          </DataQButton>
          <DataQButton
            type="secondary"
            onClick={() => onOpenExportMap("csv")}
          >
            <FontAwesomeIcon icon={faGear} />
            Configure Map
          </DataQButton>
        </Space>
        {isClientFormat && (
          <p className="text-sm text-gray-500 mt-2 mb-0">
            Client formats use the export map: choose Configure Map.
          </p>
        )}
      </div>
    </div>
  )
}

export default CsvExportPanel
